import type {
  CollectionReference,
  DocumentData,
  FieldValue,
  PartialWithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  query,
  serverTimestamp,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirestoreRoomMessage, FirestoreRoomMessageWrite } from './schema'
import { FirestoreRoomMessageType, schema, writeSchema } from './schema'
import { buildUnsupportedConverter } from '../shared/noops'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { modelListStream } from '../../firestore-mobx/stream'
import { RoomMessage } from '../../models/RoomMessage'
import { addDocWithError, updateDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const unsupported = buildUnsupportedConverter<FirestoreRoomMessage>()

const readConverter: FirestoreDataConverter<FirestoreRoomMessage> = {
  toFirestore: unsupported.toFirestore,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const writeConverter: FirestoreDataConverter<FirestoreRoomMessageWrite> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreRoomMessageWrite>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: unsupported.fromFirestore,
}

const getReadCollectionRef = (
  firestore: Firestore,
  roomId: string
): CollectionReference<FirestoreRoomMessage, DocumentData> => {
  return collection(firestore, 'rooms', roomId, 'messages').withConverter(
    readConverter
  )
}

const getWriteCollectionRef = (
  firestore: Firestore,
  roomId: string
): CollectionReference<FirestoreRoomMessageWrite, DocumentData> => {
  return collection(firestore, 'rooms', roomId, 'messages').withConverter(
    writeConverter
  )
}

export const getRoomMessages = (
  repository: FirebaseRepository,
  params: {
    roomId: string
    scheduling?: boolean
  }
) => {
  const ref = getReadCollectionRef(repository.firestore, params.roomId)

  const q = params.scheduling
    ? query(
        ref,
        where('type', 'in', [
          FirestoreRoomMessageType.Scheduling,
          FirestoreRoomMessageType.SchedulingBot,
        ])
      )
    : query(ref, where('type', '==', FirestoreRoomMessageType.Text))

  return modelListStream(repository, q, RoomMessage)
}

export const markMessageAsSeen = async (
  repository: FirebaseRepository,
  roomId: string,
  messageId: string
) => {
  const firestore = repository.firestore
  const collectionRef = collection(firestore, 'rooms', roomId, 'messages')
  const messageRef = doc(collectionRef, messageId)

  await updateDocWithError(
    messageRef,
    // dot notation allows us to update a single field in a map
    {
      [`seenBy.${repository.uid}`]: serverTimestamp(),
    },
    'MarkMessageAsSeenError'
  )
}

export const sendMessage = async (
  firestore: Firestore,
  roomId: string,
  userId: string,
  text: string,
  opts?: {
    scheduling: boolean
  }
) => {
  const seenBy = {} as Record<string, FieldValue>
  seenBy[userId] = serverTimestamp()
  const message = {
    authorId: userId,
    text,
    seenBy,
    type: opts?.scheduling
      ? FirestoreRoomMessageType.Scheduling
      : FirestoreRoomMessageType.Text,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const messagesRef = getWriteCollectionRef(firestore, roomId)

  return addDocWithError(messagesRef, message, 'SendMessageError')
}
